import { type ChatMessage } from "@do/shared-types";
import { ScrollArea } from "@do/ui/components/scroll-area";
import { cn } from "@do/ui/lib/utils";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BOTTOM_TOLERANCE, SETTLE_DEBOUNCE_MS } from "../../constants";
import { useMotionEnabled } from "../../hooks/use-motion-enabled";
import { isErrorMessage } from "../../lib/merge-bot-messages";
import {
  getRevealProps,
  INLINE_BUTTONS_EXIT_DURATION_S,
  MESSAGE_REVEAL_OFFSET_Y,
  MESSAGE_REVEAL_TIMING,
} from "../../lib/motion-presets";
import { getLayoutTop, scrollElementToTop } from "../../lib/scroll-element-to-top";
import { useChat } from "../../providers/chat-provider";
import { Message } from "./message";
import { CallConfirmation } from "./message/call-confirmation";
import { Loader } from "./message/loader";
import { MessageReview } from "./message/review";

interface MessagesProps {
  justClickedFastActionRef?: React.RefObject<boolean>;
}

/** The React key of a message in the list, also what the provider records as revealed. */
const getMessageKey = (message: ChatMessage, index: number) =>
  `message-${message.id || index}-${index}`;

/** Key of the message `showOnlyLast` keeps the buttons on: the last bot answer that is not an error. */
const getLastBotMessageKey = (messages: ChatMessage[]) => {
  for (let index = messages.length - 1; index >= 0; index--) {
    const message = messages[index];
    if (!message.user && !isErrorMessage(message)) return getMessageKey(message, index);
  }
  return null;
};

export const Messages = ({ justClickedFastActionRef }: MessagesProps) => {
  const {
    chatbotParams,
    satisfactionForm,
    messages,
    messagesRef,
    messagesListRef,
    lastMessageRef,
    lastUserMessageRef,
    autoScroll,
    isAtBottom,
    setIsAtBottom,
    scrollToBottom,
    isLoading,
    lastMessageId,
    inputRef,
    isMobile,
    pendingCallConfirmation,
    onConfirmCall,
    onCancelCall,
    setLastClickedButton,
    revealedMessageKeysRef,
  } = useChat();

  const t = useTranslations("do-chat-v2");
  const motionEnabled = useMotionEnabled();

  const scrollOnTop = Boolean(chatbotParams.scrollOnTopOfUserMessages);

  const firstMessageRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  // Track if the user manually scrolled away from the bottom
  const userPinnedToBottomRef = useRef(true);
  const didInitialMountRef = useRef(false);

  const lastMessage = messages[messages.length - 1];
  const isStreaming = useMemo(() => lastMessage?.id === "streamed-msg", [lastMessage]);

  // Completing a stream swaps the transient `streamed-msg` for the definitive message,
  // which remounts a node whose content is already on screen. Remember which message took
  // its place, so that one mounts at rest instead of flashing — and only that one: anything
  // else arriving in the same commit plays its entrance like always.
  const renderedMessageIdsRef = useRef<string | null>(null);
  const streamedReplacementKeyRef = useRef<string | null>(null);
  const messageIdsKey = messages.map((message, index) => message.id ?? index).join("|");

  // Messages the list is showing again rather than for the first time — it remounted over a
  // conversation already on screen — stay still, buttons included. Frozen at mount: anything
  // that arrives later animates in. Hosts without the provider's record fall back to "all
  // that was there at mount".
  const [restoredMessageKeys] = useState(
    () =>
      new Set(
        messages
          .map(getMessageKey)
          .filter((key) => !revealedMessageKeysRef || revealedMessageKeysRef.current.has(key))
      )
  );

  useEffect(() => {
    if (!revealedMessageKeysRef) return;
    messages.forEach((message, index) =>
      revealedMessageKeysRef.current.add(getMessageKey(message, index))
    );
  }, [messages, revealedMessageKeysRef]);

  // Counts departures rather than flagging one, so a second removal landing mid-wait
  // restarts the delay instead of inheriting the remainder of the first.
  const [messagesExitCount, setMessagesExitCount] = useState(0);

  // `showOnlyLast` with the buttons under the answers: the previous answer's buttons leave as
  // soon as a newer one lands, closing the space they held as they fade. Anything arriving
  // meanwhile would be caught rising while the conversation closes up above it, so it waits
  // the exit out — the same handoff as a message leaving. Each message reports whether its
  // buttons are showing.
  const [buttonsExitCount, setButtonsExitCount] = useState(0);
  const buttonsShownByKeyRef = useRef(new Map<string, boolean>());
  const lastBotMessageKeyRef = useRef<string | null>(null);
  const hasInlineButtonsOnlyOnLast =
    Boolean(chatbotParams.buttons?.showOnlyLast) && chatbotParams.buttons?.position !== "bottom";

  if (renderedMessageIdsRef.current !== messageIdsKey) {
    const previousMessageIds = renderedMessageIdsRef.current;

    const nextMessageIds = messageIdsKey ? messageIdsKey.split("|") : [];
    const previousIdsList = previousMessageIds ? previousMessageIds.split("|") : [];
    const streamedIndex = previousIdsList.indexOf("streamed-msg");
    const replacement = streamedIndex === -1 ? undefined : messages[streamedIndex];
    streamedReplacementKeyRef.current =
      replacement && !previousIdsList.includes(String(replacement.id ?? streamedIndex))
        ? getMessageKey(replacement, streamedIndex)
        : null;

    const didRemoveMessage = (previousMessageIds ? previousMessageIds.split("|") : []).some(
      // The streamed placeholder is the one message that leaves without an exit, so its
      // removal is instant and nothing has to be waited out.
      (id) => id !== "streamed-msg" && !nextMessageIds.includes(id)
    );

    // Updating state while rendering, which React re-renders from before committing: the
    // ref above makes it run once per change, and it keeps the loader from mounting for
    // the frame an effect would take to notice.
    if (didRemoveMessage) setMessagesExitCount((count) => count + 1);

    const previousLastBotMessageKey = lastBotMessageKeyRef.current;
    const nextLastBotMessageKey = getLastBotMessageKey(messages);
    const didButtonsLeave =
      hasInlineButtonsOnlyOnLast &&
      previousLastBotMessageKey !== null &&
      previousLastBotMessageKey !== nextLastBotMessageKey &&
      // Still on screen: a removed message takes its buttons with it, and is waited on above.
      messages.some(
        (message, index) => getMessageKey(message, index) === previousLastBotMessageKey
      ) &&
      Boolean(buttonsShownByKeyRef.current.get(previousLastBotMessageKey));
    if (didButtonsLeave) setButtonsExitCount((count) => count + 1);
    lastBotMessageKeyRef.current = nextLastBotMessageKey;

    renderedMessageIdsRef.current = messageIdsKey;
  }

  // A message that left is still on screen for the length of its exit — and `popLayout`
  // pins it where it stood, out of the flow, so the list closes up underneath it. In
  // `onlyLastMessage` mode the whole conversation is dropped and the loader turned on in
  // the same commit, which puts the loader exactly where the answer on its way out is
  // still being drawn. Hold it back until that departure is over, so the two read as a
  // handoff instead of a collision.
  const [settledMessagesExitCount, setSettledMessagesExitCount] = useState(0);
  const isWaitingForMessagesExit = motionEnabled && messagesExitCount !== settledMessagesExitCount;

  const [settledButtonsExitCount, setSettledButtonsExitCount] = useState(0);
  const isWaitingForButtonsExit = motionEnabled && buttonsExitCount !== settledButtonsExitCount;

  useEffect(() => {
    if (buttonsExitCount === settledButtonsExitCount) return;

    const timer = window.setTimeout(
      () => setSettledButtonsExitCount(buttonsExitCount),
      INLINE_BUTTONS_EXIT_DURATION_S * 1000
    );

    return () => window.clearTimeout(timer);
  }, [buttonsExitCount, settledButtonsExitCount]);

  // How long whatever arrives now holds its entrance back, for what is leaving to be gone.
  const arrivalDelayS = Math.max(
    isWaitingForMessagesExit ? MESSAGE_REVEAL_TIMING.exitDurationS : 0,
    isWaitingForButtonsExit ? INLINE_BUTTONS_EXIT_DURATION_S : 0
  );

  // Waiting on the clock rather than on `onExitComplete`: the exit is a plain tween of a
  // known length, and a timer cannot leave the loader stranded if a message is ever
  // removed without playing one.
  useEffect(() => {
    if (messagesExitCount === settledMessagesExitCount) return;

    const timer = window.setTimeout(
      () => setSettledMessagesExitCount(messagesExitCount),
      MESSAGE_REVEAL_TIMING.exitDurationS * 1000
    );

    return () => window.clearTimeout(timer);
  }, [messagesExitCount, settledMessagesExitCount]);

  const handleConfirmCall = useCallback(() => {
    if (!pendingCallConfirmation?.tel || !onConfirmCall) return;

    setLastClickedButton({
      cuid: pendingCallConfirmation.cuid ?? null,
      type: "tel",
    });
    onConfirmCall();
  }, [onConfirmCall, pendingCallConfirmation, setLastClickedButton]);

  const shouldDeferAutoScroll = useCallback(() => {
    const input = inputRef?.current;
    if (!input || document.activeElement !== input) return false;
    // Programmatic scroll while the footer input is focused dismisses the mobile keyboard.
    return isMobile ?? false;
  }, [inputRef, isMobile]);

  // When an image is loaded, force scroll to bottom when autoScroll is enabled (ignore lastMessageScrollPosition)
  const handleImageLoad = useCallback(() => {
    if (autoScroll && (isAtBottom || userPinnedToBottomRef?.current)) {
      scrollToBottom({ force: true });
    }
  }, [autoScroll, isAtBottom, scrollToBottom, userPinnedToBottomRef]);

  const updateBottomState = useCallback(() => {
    const el = messagesRef?.current;
    if (!el) return false;

    const distanceFromBottom =
      (el.scrollHeight || 0) - (el.clientHeight || 0) - (el.scrollTop || 0);
    const atBottom = distanceFromBottom <= BOTTOM_TOLERANCE;

    setIsAtBottom?.(atBottom);
    userPinnedToBottomRef.current = atBottom;

    return atBottom;
  }, [messagesRef, setIsAtBottom]);

  // When the viewport resizes, force scroll to bottom when autoScroll is enabled (ignore lastMessageScrollPosition)
  useEffect(() => {
    if (!autoScroll) return;
    const onResize = () => {
      if (shouldDeferAutoScroll()) return;
      if (userPinnedToBottomRef?.current) scrollToBottom({ force: true });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [autoScroll, scrollToBottom, userPinnedToBottomRef, shouldDeferAutoScroll]);

  // When the user scrolls, update the isAtBottom state
  // and track if the user is pinned to the bottom
  useEffect(() => {
    const el = messagesRef?.current;
    if (!el) return;
    const onScroll = () => void updateBottomState();

    const followLayout = () => {
      // The conversation can slide out from under a viewer who never touched the scroll:
      //
      // - a form takes the footer slot, which is far taller than the input it replaces, so
      //   this viewport shrinks beneath the conversation;
      // - content appears ABOVE the viewport — ending a livechat brings the bot's buttons
      //   back on the messages already on screen, several hundred pixels of them, and the
      //   scroll offset stays put while everything below it moves down.
      //
      // Either way the layout moved, not the viewer, so the drift must not be recorded as
      // a deliberate move and the pin silently dropped.
      if (autoScroll && userPinnedToBottomRef.current && !shouldDeferAutoScroll()) {
        // Not smooth: the slot folds open over several frames, and delayed buttons arrive
        // one after another, so this runs many times — an animated scroll would be
        // restarted before it ever arrived.
        scrollToBottom({ force: true });
      }

      void updateBottomState();
    };

    const ro = new ResizeObserver(followLayout);
    ro.observe(el);
    if (messagesListRef.current) ro.observe(messagesListRef.current);

    el.addEventListener("scroll", onScroll, { passive: true });
    // Follow the layout rather than only measuring it: this effect re-subscribes when a
    // message is added, and by then that message is already laid out. Ending a livechat
    // appends its separator in the same commit that brings the buttons back, so a bare
    // measure here would read that growth as the viewer leaving the bottom and drop the
    // pin before the observer ever sees it. In "top" mode nothing else scrolls down
    // afterwards, which left the conversation stranded short of its end.
    followLayout();
    return () => {
      el.removeEventListener("scroll", onScroll as EventListener);
      ro.disconnect();
    };
  }, [
    autoScroll,
    messages.length,
    messagesListRef,
    messagesRef,
    scrollToBottom,
    shouldDeferAutoScroll,
    updateBottomState,
  ]);

  // Initial open behavior - wait for first message to be rendered
  useEffect(() => {
    if (scrollOnTop) return;
    if (didInitialMountRef?.current) return;
    if (messages.length === 0) return;
    // If lastMessageScrollPosition === 'top', wait for firstMessageRef (handled in useLayoutEffect below)
    if (chatbotParams.lastMessageScrollPosition === "top") return;
    // Otherwise follow default behavior (scroll to bottom)
    didInitialMountRef.current = true;
    if (autoScroll) scrollToBottom();
  }, [
    autoScroll,
    chatbotParams.lastMessageScrollPosition,
    scrollOnTop,
    scrollToBottom,
    messages.length,
    didInitialMountRef,
  ]);

  // Handle scroll to top for first message
  // use useLayoutEffect to check after DOM updates
  useLayoutEffect(() => {
    if (scrollOnTop) return;
    if (didInitialMountRef.current) return;
    if (messages.length === 0) return;
    if (chatbotParams.lastMessageScrollPosition !== "top") return;
    if (!firstMessageRef?.current) return;

    // First message is rendered and ref is available, scroll to top
    didInitialMountRef.current = true;
    // Use requestAnimationFrame to ensure scroll happens after layout
    requestAnimationFrame(() => {
      const viewport = messagesRef?.current;
      const firstEl = firstMessageRef?.current;
      if (viewport && firstEl) {
        scrollElementToTop(viewport, firstEl, "auto");
      }
    });
  }, [
    chatbotParams.lastMessageScrollPosition,
    scrollOnTop,
    messages.length,
    didInitialMountRef,
    firstMessageRef,
    messagesRef,
  ]);

  // Core auto-scroll behavior after initial mount
  // 1) If user is near bottom (pinned), keep them at bottom as new content arrives
  // 2) If lastMessageScrollPosition === 'top', scroll to the TOP of the last message
  //    *after it finishes rendering* (no size change for SETTLE_DEBOUNCE_MS)
  // 3) Otherwise, scroll to bottom when message generation completes
  // This effect triggers on both filteredMessages.length and lastMessageId changes
  // to handle both streamed and non-streamed messages
  useEffect(() => {
    if (scrollOnTop) return;
    if (!autoScroll || !didInitialMountRef.current) return;

    if (justClickedFastActionRef?.current) {
      justClickedFastActionRef.current = false;
    }

    const el = messagesRef.current;
    const lastEl = lastMessageRef?.current;
    if (!el) return;

    // If the user is pinned to bottom and we're not in 'top' mode, stick to bottom
    if (userPinnedToBottomRef.current && chatbotParams.lastMessageScrollPosition !== "top") {
      queueMicrotask(() => scrollToBottom({ force: true }));
      return;
    }

    const isLoadingOrStreaming =
      isLoading ||
      isStreaming ||
      (lastMessage && lastMessageId && lastMessage.id !== lastMessageId);

    // Special TOP behavior: wait for the last message to finish changing size
    if (chatbotParams.lastMessageScrollPosition === "top" && lastEl && !isLoadingOrStreaming) {
      let settleTimer: number | null = null;
      const ro = new ResizeObserver(() => {
        if (settleTimer) window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          if (el && lastEl) scrollElementToTop(el, lastEl, "smooth");
        }, SETTLE_DEBOUNCE_MS);
      });
      ro.observe(lastEl);
      scrollElementToTop(el, lastEl, "auto");
      return () => {
        ro.disconnect();
        if (settleTimer) window.clearTimeout(settleTimer);
      };
    }

    if (chatbotParams.lastMessageScrollPosition !== "top") {
      queueMicrotask(() => scrollToBottom({ force: true }));
    }
    return;
  }, [
    autoScroll,
    chatbotParams.lastMessageScrollPosition,
    isLoading,
    isStreaming,
    justClickedFastActionRef,
    lastMessage,
    lastMessageId,
    lastMessageRef,
    messages,
    messagesRef,
    scrollOnTop,
    scrollToBottom,
  ]);

  // scrollOnTopOfUserMessages: pin the last user message at the top of the viewport.
  // A spacer div fills the remaining space so the messages list is tall enough
  // for the user message to be scrolled to the top of the viewport.
  // A ResizeObserver keeps the position stable during streaming and after content changes.
  useEffect(() => {
    if (!scrollOnTop) return;

    const spacer = spacerRef.current;
    const listEl = messagesListRef.current;
    const viewport = messagesRef.current;

    if (messages.length === 0 || !spacer || !listEl || !viewport) {
      if (spacer) spacer.style.height = "0px";
      return;
    }

    const userEl = lastUserMessageRef?.current;
    if (!userEl) {
      spacer.style.height = "0px";
      return;
    }

    didInitialMountRef.current = true;

    const PADDING = 10;

    // The user message is measured without its entrance transform: it is pinned where it
    // settles, not where it happens to be while still rising into place.
    const recalcAndScroll = (behavior: ScrollBehavior = "auto") => {
      spacer.style.height = "0px";
      const viewportHeight = viewport.clientHeight;
      const contentFromUserToEnd =
        listEl.getBoundingClientRect().bottom - getLayoutTop(userEl, viewport);
      const spacerHeight = Math.max(0, viewportHeight - contentFromUserToEnd - PADDING);
      spacer.style.height = `${spacerHeight}px`;
      userEl.style.paddingTop = `${PADDING}px`;
      const scrollTarget =
        getLayoutTop(userEl, viewport) - viewport.getBoundingClientRect().top + viewport.scrollTop;
      viewport.scrollTo({ top: scrollTarget, behavior });
    };

    recalcAndScroll("auto");

    const ro = new ResizeObserver(() => recalcAndScroll("auto"));
    ro.observe(listEl);
    const lastEl = lastMessageRef?.current;
    if (lastEl) ro.observe(lastEl);

    return () => {
      ro.disconnect();
      spacer.style.height = "0px";
      userEl.style.paddingTop = "";
    };
  }, [
    scrollOnTop,
    messages,
    lastUserMessageRef,
    lastMessageRef,
    messagesRef,
    messagesListRef,
    isLoading,
    isStreaming,
    lastMessageId,
  ]);

  // When the text input itself resizes, keep the viewport pinned if user was at bottom
  useEffect(() => {
    if (!didInitialMountRef.current) return;

    const input = inputRef?.current;
    if (!input) return;
    const ro = new ResizeObserver(() => {
      if (shouldDeferAutoScroll()) return;
      if (userPinnedToBottomRef.current) scrollToBottom({ force: true, smooth: true });
    });
    ro.observe(input);
    return () => ro.disconnect();
  }, [scrollToBottom, userPinnedToBottomRef, inputRef, shouldDeferAutoScroll]);

  return (
    <ScrollArea
      id="messages"
      className="flex-1 min-h-0"
      ref={messagesRef}
      scrollbarProps={{
        className: "w-(--scroll-bar-width)",
        thumbClassName: cn(
          "bg-(--scroll-bar-thumb-background)/(--scroll-bar-thumb-background-opacity)",
          "rounded-(--scroll-bar-thumb-border-radius)"
        ),
      }}
    >
      <div
        id="messages-list"
        ref={messagesListRef}
        className={cn(
          // Positioned so a message leaving under `popLayout` — which pins it `absolute` at
          // its measured offsets — resolves against this list. The Radix viewport above is
          // static and only its Root is positioned, so without this the leaving message
          // takes the Root as its containing block, escapes the viewport's overflow
          // entirely, and is painted over the footer.
          "relative",
          "h-auto flex flex-col mx-auto w-full",
          "max-w-(--messages-max-width)",
          "gap-(--messages-gap)",
          "px-(--messages-padding-x) py-(--messages-padding-y)",
          "@container/messages"
        )}
        role="log"
        aria-live="polite"
        aria-label={t("chat.messages.accessibility.messages-aria-label")}
        aria-busy={isLoading}
      >
        {/* `initial={false}` only over a conversation it already showed — it remounted — so
            that nothing in those messages replays. The list often mounts with the greeting
            already in it, which must animate in like any other arrival.
            `popLayout` pins a leaving message out of the flow so the list closes up
            around it straight away — in flow it would hold its slot for the whole
            exit, pushing whatever replaces it down and then snapping back up. */}
        <AnimatePresence initial={restoredMessageKeys.size === 0} mode="popLayout">
          {messages.map((message, index) => (
            <Message
              key={getMessageKey(message, index)}
              firstMessageRef={firstMessageRef}
              message={message}
              index={index}
              handleImageLoad={handleImageLoad}
              isRestored={restoredMessageKeys.has(getMessageKey(message, index))}
              onButtonsShownChange={(shown) =>
                buttonsShownByKeyRef.current.set(getMessageKey(message, index), shown)
              }
              skipReveal={streamedReplacementKeyRef.current === getMessageKey(message, index)}
              // A message arriving in the same commit that removed another would otherwise
              // rise into place while that one is still sliding out over it — `popLayout`
              // pins the leaver where it stood, so they occupy the same spot. Waiting out
              // the departure turns the two into one handoff. In `onlyLastMessage` mode that
              // is every single exchange: the answer is replaced by the user's own message,
              // and then that message by the next answer.
              revealDelayS={arrivalDelayS}
            />
          ))}
        </AnimatePresence>

        {/* Holds the panel while it leaves — it reads its own presence from here to stop
            a call being confirmed on a card that is already on its way out. */}
        <AnimatePresence>
          {pendingCallConfirmation?.tel && onConfirmCall && onCancelCall && (
            <CallConfirmation
              phoneNumber={pendingCallConfirmation.tel}
              onConfirm={handleConfirmCall}
              onCancel={onCancelCall}
            />
          )}
        </AnimatePresence>

        <MessageReview satisfactionForm={satisfactionForm} />

        {isLoading && !isStreaming && !isWaitingForMessagesExit && (
          // Wrapped here rather than inside `Loader`, because this is the only place the
          // loader joins the conversation on its own. Its two other call sites sit inside a
          // message that is already playing this very entrance, and animating there would
          // compound with it — twice the fade, twice the travel.
          <motion.div
            {...getRevealProps({
              motionEnabled,
              timing: MESSAGE_REVEAL_TIMING,
              offsetY: MESSAGE_REVEAL_OFFSET_Y,
            })}
          >
            <Loader allowThinking={messages.length > 0} />
          </motion.div>
        )}
        {scrollOnTop && <div ref={spacerRef} />}
      </div>
    </ScrollArea>
  );
};
